/**
 * @file care-proposal.schema.ts
 * @description Proposta de jornada de cuidado — o contrato entre quem monta
 * (o agente de jornadas, um roteiro convertido, uma pessoa) e o importador,
 * que é o único que grava.
 *
 * Plano: "Agente de Jornadas" (artifact de 25/09/2026), fase F0.
 *
 * TRÊS REGRAS QUE ESTE SCHEMA SUSTENTA:
 *
 *   1. A proposta carrega a definição FINAL, com IDs. Blocos, regras e ações
 *      são os mesmos schemas de `careItems`; nada aqui é referência por nome.
 *      Referência externa que não se resolveu não entra como ação — entra como
 *      pendência. Assim o importador nunca precisa adivinhar.
 *
 *   2. Nada é criado pela metade. Enquanto houver pendência sem resolução, o
 *      importador recusa (`pendenciasAbertas`). Retirar uma indicação ou seguir
 *      sem mudar um ponto clínico conta como resolver, desde que explícito e
 *      com justificativa — decisão de 25/09/2026.
 *
 *   3. Variável nova é só proposta. Ela vira documento em `careVariables`
 *      apenas se a pendência `VARIAVEL_NOVA` correspondente foi aceita.
 */

import { z } from 'zod';
import {
  CareActivationSchema,
  CareAudienceEnum,
  CareItemRuleSchema,
  SomaDePontuacaoSchema,
  CareItemTypeEnum,
  CareJourneyEnum,
  CarePriorityEnum,
  CareReminderConfigSchema,
} from './care-item.schema';
import { CareBlockSchema } from './care-block.schema';
import {
  CareVariableCalculationSchema,
  CareVariableDataTypeEnum,
  CareVariableScopeEnum,
} from './care-variable.schema';

// ---------------------------------------------------------------------------
// Origem
// ---------------------------------------------------------------------------

/**
 * De onde a proposta veio. O documento enviado NÃO é guardado (decisão de
 * 25/09/2026): fica só o suficiente para reconhecer o mesmo arquivo depois.
 */
export const CareProposalOrigemSchema = z.object({
  /**
   * `MCP`: enviada pela IA de alguém, conectada pelo conector MCP (plano
   * "Conector MCP de Jornadas"). É o que faz a versão criada levar o selo
   * "gerado com IA".
   */
  tipo: z.enum(['DOCUMENTO', 'PROMPT', 'ROTEIRO', 'MCP']),
  /** Nome da IA que enviou, como o cliente MCP se identificou ("Claude", "ChatGPT"). */
  cliente: z.string().max(120).nullable().optional(),
  nomeArquivo: z.string().nullable().optional(),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'sha256 em hexadecimal minúsculo')
    .nullable()
    .optional(),
  tamanhoBytes: z.number().int().nonnegative().nullable().optional(),
});
export type CareProposalOrigem = z.infer<typeof CareProposalOrigemSchema>;

// ---------------------------------------------------------------------------
// Itens
// ---------------------------------------------------------------------------

/**
 * Onde o item vai parar.
 *
 * `CRIAR` já traz o id que o documento terá. É isso que permite a um item da
 * proposta apontar para outro da mesma proposta (`ACTIVATE_ITEM`,
 * `CREATE_REMINDER`, ativação `AFTER_ITEM`) com o id real, sem placeholder
 * para traduzir na hora de gravar.
 *
 * `NOVA_VERSAO` aponta um item existente da marca, que ganha a próxima versão
 * (append-only). Em rascunho, é como os rascunhos vazios deixam de virar
 * duplicata; em publicado, é a atualização de uma jornada no ar — gravada sem
 * publicar, e a versão publicada segue valendo até alguém publicar a nova.
 */
export const CareProposalAlvoSchema = z.preprocess(
  // `REAPROVEITAR` (F0) virou `NOVA_VERSAO` quando a atualização passou a
  // valer também para item publicado. Mapas e registros de importação já
  // gravados usam o nome antigo.
  (v) => (v && typeof v === 'object' && (v as any).modo === 'REAPROVEITAR' ? { ...(v as object), modo: 'NOVA_VERSAO' } : v),
  z.discriminatedUnion('modo', [
    z.object({ modo: z.literal('CRIAR'), itemId: z.string().uuid() }),
    z.object({ modo: z.literal('NOVA_VERSAO'), itemId: z.string().min(1) }),
  ])
);
export type CareProposalAlvo = z.infer<typeof CareProposalAlvoSchema>;

/**
 * Proveniência de um bloco. `redigidoPeloAgente` separa o texto transcrito do
 * documento clínico do texto que alguém (o agente) escreveu — este último
 * sempre passa por aprovação humana como pendência `TEXTO_REDIGIDO`.
 */
export const CareProposalProvenienciaSchema = z.object({
  blockId: z.string(),
  redigidoPeloAgente: z.boolean().default(false),
  linhaInicial: z.number().int().positive().nullable().optional(),
  linhaFinal: z.number().int().positive().nullable().optional(),
});

export const CareProposalItemSchema = z.object({
  alvo: CareProposalAlvoSchema,
  type: CareItemTypeEnum,
  internalName: z.string().min(1),
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  journey: CareJourneyEnum,
  audience: CareAudienceEnum.default('TITULAR'),
  activation: CareActivationSchema,
  priority: CarePriorityEnum.default('OPTIONAL'),
  allowsNewResponse: z.boolean().default(false),
  estimatedMinutes: z.number().int().positive().nullable().optional(),
  blocks: z.array(CareBlockSchema).default([]),
  itemRules: z.array(CareItemRuleSchema).default([]),
  /** Somas de pontuação do checkpoint (escores como PHQ-9 e EPDS). */
  somas: z.array(SomaDePontuacaoSchema).default([]),
  reminderConfig: CareReminderConfigSchema.nullable().optional(),
  proveniencia: z.array(CareProposalProvenienciaSchema).default([]),
});
export type CareProposalItem = z.infer<typeof CareProposalItemSchema>;

// ---------------------------------------------------------------------------
// Variáveis propostas
// ---------------------------------------------------------------------------

export const CareProposalVariavelSchema = z.object({
  name: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/, 'Use snake_case minúsculo: comece com letra e utilize apenas a–z, 0–9 e _'),
  label: z.string().min(1),
  description: z.string().nullable().optional(),
  dataType: CareVariableDataTypeEnum,
  scope: CareVariableScopeEnum.default('USER'),
  allowedValues: z.array(z.string()).default([]),
  calculation: CareVariableCalculationSchema.nullable().optional(),
});
export type CareProposalVariavel = z.infer<typeof CareProposalVariavelSchema>;

// ---------------------------------------------------------------------------
// Pendências
// ---------------------------------------------------------------------------

/**
 * O que ainda impede a criação. Cada tipo corresponde a uma linha da tabela
 * "Nada é criado pela metade" do plano, com um exemplo real dos roteiros de
 * set/2026 entre parênteses.
 */
export const CareProposalPendenciaTipoEnum = z.enum([
  /** Aula citada que não existe no catálogo ("Curso nutri Dani · Cãibra"). */
  'CONTEUDO_INEXISTENTE',
  /** Aula de outra marca sem concessão CONTENT_CATALOG ativa (aulas da Nest). */
  'CONTEUDO_OUTRA_MARCA',
  /** Ferramenta citada que não existe ("Lembrete de hidratação"). */
  'FERRAMENTA_INEXISTENTE',
  /** Mais de um candidato plausível; alguém precisa escolher. */
  'REFERENCIA_AMBIGUA',
  /** Variável sugerida que ainda não existe (`ciclo_duracao`). */
  'VARIAVEL_NOVA',
  /** Texto que o agente escreveu, não transcreveu (pergunta de triagem da DUM). */
  'TEXTO_REDIGIDO',
  /** Ponto clínico levantado na leitura ("Enjoo ou vômitos" sem orientação). */
  'ATENCAO_CLINICA',
  /** Pedido vago: falta o conteúdo clínico, que o agente não inventa. */
  'TEXTO_CLINICO_NECESSARIO',
  /** Confirmação de reaproveitar um rascunho existente em vez de criar. */
  'RASCUNHO_EXISTENTE',
  /** Confirmação de gravar uma versão nova numa jornada publicada. */
  'ITEM_PUBLICADO',
]);
export type CareProposalPendenciaTipo = z.infer<typeof CareProposalPendenciaTipoEnum>;

/**
 * Como a pendência foi fechada.
 *
 *   RESOLVIDA      — o que faltava agora existe e já está na proposta
 *                    (curso cadastrado e ação incluída, texto escrito).
 *   ACEITA         — a pessoa aprovou o que foi proposto (variável nova,
 *                    texto redigido, reaproveitamento do rascunho).
 *   RECUSADA       — a pessoa recusou a proposta (variável nova recusada).
 *   RETIRADA       — a indicação sai do item. Exige justificativa.
 *   DECISAO_CLINICA— seguir sem mudança num ponto clínico. Exige justificativa.
 */
export const CareProposalResolucaoTipoEnum = z.enum([
  'RESOLVIDA',
  'ACEITA',
  'RECUSADA',
  'RETIRADA',
  'DECISAO_CLINICA',
]);
export type CareProposalResolucaoTipo = z.infer<typeof CareProposalResolucaoTipoEnum>;

/** Resoluções que só valem acompanhadas de um porquê. */
export const RESOLUCOES_COM_JUSTIFICATIVA: readonly CareProposalResolucaoTipo[] = ['RETIRADA', 'DECISAO_CLINICA'];

/**
 * Resoluções possíveis por tipo de pendência, na revisão (M0). As que mudam
 * o conteúdo da proposta não estão aqui de propósito: marcar "resolvida" sem
 * a mudança existir seria mentir para o importador.
 */
export const RESOLUCOES_PERMITIDAS: Readonly<Record<CareProposalPendenciaTipo, readonly CareProposalResolucaoTipo[]>> = {
  CONTEUDO_INEXISTENTE: ['RETIRADA'],
  CONTEUDO_OUTRA_MARCA: ['RETIRADA'],
  FERRAMENTA_INEXISTENTE: ['RETIRADA'],
  REFERENCIA_AMBIGUA: ['RETIRADA'],
  VARIAVEL_NOVA: ['ACEITA', 'RECUSADA'],
  TEXTO_REDIGIDO: ['ACEITA', 'RECUSADA'],
  ATENCAO_CLINICA: ['DECISAO_CLINICA'],
  TEXTO_CLINICO_NECESSARIO: [],
  RASCUNHO_EXISTENTE: ['ACEITA', 'RECUSADA'],
  ITEM_PUBLICADO: ['ACEITA', 'RECUSADA'],
};

export const CareProposalResolucaoSchema = z
  .object({
    tipo: CareProposalResolucaoTipoEnum,
    por: z.string().min(1),
    em: z.coerce.date(),
    justificativa: z.string().trim().nullable().optional(),
  })
  .superRefine((r, ctx) => {
    if (RESOLUCOES_COM_JUSTIFICATIVA.includes(r.tipo) && !r.justificativa) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['justificativa'],
        message: `Resolução ${r.tipo} exige justificativa — é ela que vai para a nota da versão.`,
      });
    }
  });
export type CareProposalResolucao = z.infer<typeof CareProposalResolucaoSchema>;

export const CareProposalPendenciaSchema = z.object({
  id: z.string().min(1),
  tipo: CareProposalPendenciaTipoEnum,
  descricao: z.string().min(1),
  /** O trecho do documento que originou a pendência, como apareceu lá. */
  textoOriginal: z.string().nullable().optional(),
  /** Onde a pendência mora. `itemId` é o `alvo.itemId` do item. */
  local: z
    .object({
      itemId: z.string().nullable().optional(),
      blockId: z.string().nullable().optional(),
      optionId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  /** Para `VARIAVEL_NOVA`: o nome em `variaveisNovas`. */
  variavel: z.string().nullable().optional(),
  /**
   * Para `TEXTO_REDIGIDO`: `TITULO` quando o texto escrito é só o título de
   * um bloco — não clínico, e por isso pode ser aceito em lote na revisão,
   * depois de a pessoa ler a lista. `CONTEUDO` (ou ausente) segue decisão uma
   * a uma. Opcional: propostas anteriores não têm, e valem como `CONTEUDO`.
   */
  natureza: z.enum(['TITULO', 'CONTEUDO']).optional(),
  /** Para `REFERENCIA_AMBIGUA`: o que o agente encontrou. */
  candidatos: z.array(z.object({ rotulo: z.string(), ref: z.record(z.unknown()) })).default([]),
  resolucao: CareProposalResolucaoSchema.nullable().default(null),
});
export type CareProposalPendencia = z.infer<typeof CareProposalPendenciaSchema>;

// ---------------------------------------------------------------------------
// Proposta
// ---------------------------------------------------------------------------

export const CareProposalAtencaoSchema = z.object({
  titulo: z.string(),
  explicacao: z.string(),
  gravidade: z.enum(['CLINICA', 'PRODUTO']),
});

export const CareProposalTesteSchema = z.object({
  cenario: z.string(),
  esperado: z.array(z.string()).default([]),
});

export const CareProposalSchema = z
  .object({
    /**
     * Identidade da proposta. É a chave de idempotência do importador: aplicar
     * duas vezes a mesma proposta não cria nada na segunda.
     */
    id: z.string().uuid(),
    origem: CareProposalOrigemSchema,
    itens: z.array(CareProposalItemSchema).min(1),
    variaveisNovas: z.array(CareProposalVariavelSchema).default([]),
    pendencias: z.array(CareProposalPendenciaSchema).default([]),
    /** Informativo — o que exige decisão está em `pendencias`. */
    atencao: z.array(CareProposalAtencaoSchema).default([]),
    testes: z.array(CareProposalTesteSchema).default([]),
  })
  .superRefine((p, ctx) => {
    // Ids de item únicos: dois itens para o mesmo alvo gravariam por cima um
    // do outro na mesma aplicação.
    const vistos = new Set<string>();
    p.itens.forEach((item, i) => {
      if (vistos.has(item.alvo.itemId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itens', i, 'alvo', 'itemId'],
          message: `Dois itens da proposta apontam para o mesmo id (${item.alvo.itemId}).`,
        });
      }
      vistos.add(item.alvo.itemId);
    });

    const pendIds = new Set<string>();
    p.pendencias.forEach((pd, i) => {
      if (pendIds.has(pd.id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pendencias', i, 'id'], message: `Pendência duplicada: ${pd.id}` });
      }
      pendIds.add(pd.id);
    });

    // Toda variável nova precisa de uma pendência VARIAVEL_NOVA: é o que
    // garante que ninguém crie variável sem aceite explícito.
    const nomes = new Set(p.variaveisNovas.map((v) => v.name));
    const cobertas = new Set(p.pendencias.filter((pd) => pd.tipo === 'VARIAVEL_NOVA').map((pd) => pd.variavel));
    p.variaveisNovas.forEach((v, i) => {
      if (!cobertas.has(v.name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['variaveisNovas', i],
          message: `A variável nova "${v.name}" não tem pendência VARIAVEL_NOVA para ser aceita.`,
        });
      }
    });
    p.pendencias.forEach((pd, i) => {
      if (pd.tipo === 'VARIAVEL_NOVA' && (!pd.variavel || !nomes.has(pd.variavel))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pendencias', i, 'variavel'],
          message: 'Pendência VARIAVEL_NOVA precisa apontar uma variável de `variaveisNovas`.',
        });
      }
    });
  });
export type CareProposal = z.infer<typeof CareProposalSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Pendências ainda sem resolução. Lista vazia é o que libera a criação. */
export function pendenciasAbertas(proposta: Pick<CareProposal, 'pendencias'>): CareProposalPendencia[] {
  return proposta.pendencias.filter((p) => !p.resolucao);
}

/**
 * Variáveis novas que serão criadas: só as de pendência aceita. Recusada ou
 * sem resolução não entra — e se algum bloco depender dela, o importador
 * recusa pela checagem de variáveis, não por esta lista.
 */
export function variaveisAceitas(proposta: Pick<CareProposal, 'variaveisNovas' | 'pendencias'>): CareProposalVariavel[] {
  const aceitas = new Set(
    proposta.pendencias
      .filter((p) => p.tipo === 'VARIAVEL_NOVA' && p.resolucao?.tipo === 'ACEITA')
      .map((p) => p.variavel)
  );
  return proposta.variaveisNovas.filter((v) => aceitas.has(v.name));
}

/**
 * Linhas para a nota da versão: toda decisão que tirou algo do documento ou
 * seguiu sem mudança fica registrada com quem, e por quê.
 */
export function decisoesParaNota(proposta: Pick<CareProposal, 'pendencias'>, itemId: string): string[] {
  return proposta.pendencias
    .filter((p) => p.resolucao && RESOLUCOES_COM_JUSTIFICATIVA.includes(p.resolucao.tipo))
    .filter((p) => !p.local?.itemId || p.local.itemId === itemId)
    .map((p) => `${p.resolucao!.tipo === 'RETIRADA' ? 'Retirado' : 'Decisão clínica'}: ${p.descricao} — ${p.resolucao!.justificativa} (${p.resolucao!.por})`);
}

// ---------------------------------------------------------------------------
// Registro de importação (`careProposalImports`)
// ---------------------------------------------------------------------------

/**
 * Um documento por proposta aplicada, com id = `proposta.id`. É a trava de
 * idempotência (criado com `create()`, que falha se já existe) e o rastro
 * de auditoria: guarda a proposta exatamente como foi aplicada, quem aplicou,
 * em qual marca, e o que foi gravado — ou o que falhou e como foi desfeito.
 *
 * A proposta tem só definição de jornada (texto clínico, blocos, regras):
 * nenhum dado de paciente, e o documento de origem não está nela.
 */
export const CareProposalImportStatusEnum = z.enum(['EM_ANDAMENTO', 'CONCLUIDA', 'FALHOU']);
export type CareProposalImportStatus = z.infer<typeof CareProposalImportStatusEnum>;

export const CareProposalImportGravacaoSchema = z.object({
  itemId: z.string(),
  acao: z.enum(['CRIAR', 'NOVA_VERSAO']),
  versao: z.number().int().positive(),
});

export const CareProposalImportSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string(),
  aplicadoPor: z.string(),
  status: CareProposalImportStatusEnum,
  proposta: z.record(z.unknown()),
  itens: z.array(CareProposalImportGravacaoSchema).default([]),
  variaveisCriadas: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
  erro: z.string().nullable().default(null),
  /** O que a compensação desfez quando a aplicação falhou no meio. */
  compensacao: z.array(z.string()).default([]),
  iniciadoEm: z.date(),
  concluidoEm: z.date().nullable().default(null),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type CareProposalImport = z.infer<typeof CareProposalImportSchema>;

// ---------------------------------------------------------------------------
// Proposta persistida (`careProposals`) — plano "Conector MCP de Jornadas"
// ---------------------------------------------------------------------------

/**
 * A proposta que chegou pelo conector (ou por qualquer origem) e espera a
 * revisão humana. Um documento por proposta, id = `proposta.id`.
 *
 * O ciclo: AGUARDANDO_PENDENCIAS → (pessoa resolve) → PRONTA → (pessoa
 * aplica) → APLICADA. A IA pode mandar uma proposta nova que SUBSTITUI a
 * anterior (depois de cadastrarem um curso, por exemplo); a pessoa pode
 * DESCARTAR. Resolver pendência é só humano, e cada resolução guarda quem e
 * quando dentro da própria proposta.
 */
export const CareProposalRegistroStatusEnum = z.enum(['AGUARDANDO_PENDENCIAS', 'PRONTA', 'APLICADA', 'DESCARTADA', 'SUBSTITUIDA']);
export type CareProposalRegistroStatus = z.infer<typeof CareProposalRegistroStatusEnum>;

export const CareProposalRegistroSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string(),
  enviadoPor: z.string(),
  status: CareProposalRegistroStatusEnum,
  proposta: z.record(z.unknown()),
  /** Resumo para listar sem abrir a proposta. */
  resumo: z.object({
    titulos: z.array(z.string()).default([]),
    acoes: z.array(z.enum(['CRIAR', 'NOVA_VERSAO'])).default([]),
    cliente: z.string().nullable().default(null),
  }),
  substitui: z.string().nullable().default(null),
  substituidaPor: z.string().nullable().default(null),
  importacaoId: z.string().nullable().default(null),
  aplicadaPor: z.string().nullable().default(null),
  aplicadaEm: z.date().nullable().default(null),
  descartadaPor: z.string().nullable().default(null),
  motivoDoDescarte: z.string().nullable().default(null),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type CareProposalRegistro = z.infer<typeof CareProposalRegistroSchema>;
